import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHead,
  SkeletonList,
  StatusPill,
  useToast,
} from "@ui/react";
import { ChevronLeftIcon, PlusIcon, ToolsIcon, TrashIcon } from "@ui/react/icons";
import { deleteService, listServices, updateService } from "../../lib/api/contractor";
import type { ContractorService } from "../../lib/types/models";
import { duration, money } from "../../lib/format";
import { errorMessage } from "../../lib/errorMessage";
import { routerLink } from "../../lib/routerLink";

/** "R450 – R1 200 · 45 min – 2 h" across a service's variants. */
function variantSummary(service: ContractorService): string {
  const variants = service.variants ?? [];
  if (variants.length === 0) return "No options priced yet";

  const currency = variants[0]?.priceCurrency ?? "ZAR";
  const prices = variants.map((variant) => variant.priceAmount);
  const durations = variants.map((variant) => variant.durationMinutes);
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);
  const shortest = Math.min(...durations);
  const longest = Math.max(...durations);

  const price =
    lowPrice === highPrice
      ? money(lowPrice, currency)
      : `${money(lowPrice, currency)} – ${money(highPrice, currency)}`;
  const time =
    shortest === longest
      ? duration(shortest)
      : `${duration(shortest)} – ${duration(longest)}`;

  return `${variants.length} ${variants.length === 1 ? "option" : "options"} · ${price} · ${time}`;
}

export function ServicesListScreen() {
  const toast = useToast();
  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    data: ContractorService[] | null;
  }>({ loading: true, error: null, data: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<ContractorService | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    listServices()
      .then((services) => {
        if (!ignore) setState({ loading: false, error: null, data: services });
      })
      .catch((error: unknown) => {
        if (!ignore) setState({ loading: false, error, data: null });
      });

    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const closeDialog = useCallback(() => setPendingDelete(null), []);

  const onDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setActionError(null);
    try {
      // Hard delete, answering 204 with no body — nothing to merge back in,
      // so re-list instead of patching local state.
      await deleteService(pendingDelete.serviceId);
      setPendingDelete(null);
      toast.show("Service deleted.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggle = async (service: ContractorService) => {
    setTogglingId(service.serviceId);
    try {
      await updateService(service.serviceId, {
        name: service.name,
        description: service.description ?? "",
        displayOrder: service.displayOrder ?? 0,
        enabled: !service.enabled,
        variants: service.variants ?? [],
      });
      toast.show(service.enabled ? "Service disabled." : "Service enabled.");
      await reload();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setTogglingId(null);
    }
  };

  // The screen has exactly one "add" affordance in every state. While the list
  // is empty that is the empty state's own button, so the page head keeps
  // quiet: it used to render "Add service" above an empty state offering "Add
  // your first service" — two calls to action a few hundred pixels apart,
  // going to the same route.
  const populated = Boolean(state.data && state.data.length > 0);

  return (
    <div className="stack stack--loose">
      {/* No eyebrow: the back link below names this same section, and a head
          that says "Booking setup" twice in adjacent lines is stammering. See
          PageHead for the rule. */}
      <PageHead
        title="Services"
        back={
          <Link className="back-link" to="/settings/booking">
            <ChevronLeftIcon />
            Booking setup
          </Link>
        }
        actions={
          populated ? (
            <Link className="btn btn-primary" to="/settings/services/new">
              <PlusIcon />
              Add service
            </Link>
          ) : undefined
        }
      />

      {actionError ? (
        <Banner tone="danger" title="That didn't work">
          {actionError}
        </Banner>
      ) : null}

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading your services</span>
          <SkeletonList rows={3} height="8rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={reload} />
      ) : !state.data || state.data.length === 0 ? (
        <EmptyState
          icon={<ToolsIcon />}
          title="No services yet"
          action={{ label: "Add your first service", href: "/settings/services/new", primary: true }}
          renderLink={routerLink}
        >
          A service is one thing you do, with a price and a duration for each option customers
          can pick. Nothing can be booked until you've added one.
        </EmptyState>
      ) : (
        <ul className="stack">
          {state.data.map((service) => (
            <li key={service.serviceId}>
              {/* The whole body is the link and the actions sit outside it, the
                  JobCard/bookings-inbox idiom — a button nested in an anchor is
                  invalid HTML. The service name used to be the only way into
                  its prices and durations and was a 26px target on a row of
                  44px controls; it is now the row. The separate "Edit" button
                  went with it: it went exactly where the row goes. */}
              <div className="card card--dense card--flush">
                <Link className="row" to={`/settings/services/${service.serviceId}`}>
                  <div className="stack stack--tight">
                    <div className="row__top">
                      <span className="row__title">{service.name}</span>
                      <StatusPill tone={service.enabled ? "success" : "neutral"}>
                        {service.enabled ? "Enabled" : "Disabled"}
                      </StatusPill>
                    </div>
                    {service.description ? (
                      <span className="muted">{service.description}</span>
                    ) : null}
                    <span className="dim">{variantSummary(service)}</span>
                  </div>
                </Link>
                <div className="cluster job-card__footer">
                  <Button
                    variant="tertiary"
                    loading={togglingId === service.serviceId}
                    disabled={togglingId !== null}
                    onClick={() => void toggle(service)}
                  >
                    {service.enabled ? "Disable" : "Enable"}
                  </Button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Delete ${service.name}`}
                    onClick={() => {
                      setActionError(null);
                      setPendingDelete(service);
                    }}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pendingDelete ? (
        <ConfirmDialog
          title={`Delete "${pendingDelete.name}"?`}
          confirmLabel="Delete service"
          cancelLabel="Keep it"
          destructive
          confirming={deleting}
          onConfirm={() => void onDelete()}
          onCancel={closeDialog}
          body={
            <p>
              This removes the service and all of its prices for good — there's no undo, and
              customers won't be able to book it any more. Bookings already made are not
              affected.
            </p>
          }
        />
      ) : null}
    </div>
  );
}
